import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { timeZone, tripTimeline, getTimeline, vehicleTripData, getVehicleTripData, isTimelineLoading } from "store/vehicleTripSlice";
import { getTrip, setApproving } from "store/tripSlice";
import { Box } from "@mui/material";
import { TripB2BError } from "trips/models";
import moment from "moment";
import orgDataService from "services/orgDataService";
import { tripData } from "store/tripSlice";
import { toast } from "react-toastify";
import { pointsOfService, getPointsOfService } from "store/tripSlice";
import EventsService from "services/eventsService";
import tripService from "services/tripService";
import SuggestedEventsService from "services/suggestedEventsService";
import CloserEventsGrid from "./CloserEventsGrid";
import MDBox from "components/MDBox";
import { AuthContext } from "common/AuthProvider";
import ConfirmDialog from "common/components/ConfirmDialog";
import { Role } from "common/Constants";
import { conflictMessage, TripResetChip, TripResetQueuedBanner, useTripResetStatus } from "./tripResetStatus";

const CloserLayout = () => {
  const dispatch = useDispatch();
  const trip = useSelector(tripData);
  const { userHasPermission, isUserInRole } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true);
  const _tripTimeline = useSelector(tripTimeline);
  const _timeZone = useSelector(timeZone);
  const [waitReasons, setWaitReasons] = useState([]);
  const [cancelReasons, setCancelReasons] = useState([]);
  const [tripEvents, setTripEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const _pointsOfService = useSelector(pointsOfService);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [showValidationErrors, setShowValidationErrors] = useState(true);
  const [expandedValidationErrors, setExpandedValidationErrors] = useState(true);
  const [approveLoading, setApproveLoading] = useState(false);
  // What the approve buttons say while they work. Approving is two server round-trips now -- save
  // the events, then finalize -- and a single label for both left the screen silent through the
  // longer half of it.
  const [approveStatus, setApproveStatus] = useState<string | null>(null);
  // Validation is a server round-trip of its own and used to run silently, so the screen sat
  // there looking finished while the rules were still being checked -- and the answer, when it
  // landed, looked like it had come from nowhere.
  const [validating, setValidating] = useState(false);
  // A reset is three round-trips -- rebuild, reload the timeline, re-validate -- and a single
  // loading flag left the overlay saying "Rebuilding" for all of them, including after the toast
  // had already reported what the rebuild did. That reads as a hang.
  const [resetStatus, setResetStatus] = useState<string | null>(null);
  const [eventResetLoading, setEventResetLoading] = useState(false);
  const [eventResetConfirmOpen, setEventResetConfirmOpen] = useState(false);
  const [gridCollapsed, setGridCollapsed] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>("active");
  const [canOverride, setCanOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [validationLoaded, setValidationLoaded] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [mapDataReady, setMapDataReady] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [managerReviewLoading, setManagerReviewLoading] = useState(false);
  const timelineLoading = useSelector(isTimelineLoading);
  const _vehicleActivity = useSelector(vehicleTripData);

  // Check user permissions
  const hasAllowApprove = userHasPermission("AllowApprove");
  const hasAllowOverride = userHasPermission("AllowLitmusOverride");
  const hasAllowClearAllEvents = userHasPermission("AllowClearAllEvents");
  // Role rather than a named permission because no permission exists for this yet;
  // swap to userHasPermission once one is seeded.
  const isAdmin = isUserInRole(Role.Admin);
  const isTripAdmin = isUserInRole(Role.TripAdmin);
  // Event Reset is a closer's tool as much as an admin's -- closers are the people who find a trip
  // whose middle is wrong and need it rebuilt. TripAdmin is the closer role.
  const canResetEvents = isAdmin || isTripAdmin;

  // A bulk Event Reset that has queued this trip owns its events until it finishes: OrderApi
  // refuses saves (409) and the grid goes read-only with a banner saying who queued it and when.
  // When the reset finishes the events on screen are the ones it replaced: unsaved edits are
  // dropped and the timeline reloaded before the grid becomes editable again, so a save can't send
  // stale event ids and bring back events the reset removed.
  const { status: tripResetStatus, queued: queuedForReset, refresh: refreshTripResetStatus } = useTripResetStatus(
    trip?.id,
    undefined,
    async () => {
      setTripEvents([]);
      await dispatch(getTimeline({ tripNumber: trip.id.toString(), isActive: activeFilter, isReload: true }));
      toast.warning("This trip was reset while open; it has been reloaded.", { autoClose: false });
      await runValidation();
    }
  );

  const requriedEventNames = [
    "Begin Trip",
    "End Trip",
    "Arrived at Pickup",
    "Depart with Crew",
    "Arrived at Destination",
    "Drop off Crew",
  ];

  useEffect(() => {
    if (!trip) return;

    Promise.all([
      orgDataService.getWaitReasons(trip.clientId),
      orgDataService.getTripCancelReasons(),
      orgDataService.getHubsByClient(trip.clientId),
      orgDataService.getEventTypes(),
      dispatch(getPointsOfService({ clientId: trip.clientId, hubId: trip.hub?.id })),
    ]).then(([waits, cancels, hubs, events]) => {
      setWaitReasons(waits);
      setCancelReasons(cancels);
      setLocations(hubs);
      setEventTypes(events);
      setIsLoading(false);
    });
  }, [trip, dispatch]);

  // Reset closer-local state when the trip changes so the previous trip's events,
  // validation, and stats can't bleed into the new trip's view during fetch round-trips.
  const prevTripIdRef = React.useRef<number | undefined>(trip?.id);
  useEffect(() => {
    if (prevTripIdRef.current === trip?.id) return;
    prevTripIdRef.current = trip?.id;
    setTripEvents([]);
    setValidationErrors([]);
    setValidationWarnings([]);
    setValidationLoaded(false);
    setShowValidationErrors(true);
    setExpandedValidationErrors(true);
    setCanOverride(false);
    setOverrideReason("");
    setAcceptedWaitCount(0);
    setAcceptedWaitMinutes(0);
    setDismissedWaitCount(0);
    setDismissedWaitMinutes(0);
  }, [trip?.id]);

  // Sync tripEvents from timeline when backend data changes (initial load or post-save reload)
  // Dismissed suggestions now come back with isActive: false from the backend — no client-side matching needed
  useEffect(() => {
    if (!_tripTimeline) return;
    setTripEvents(_tripTimeline);
  }, [_tripTimeline]);

  // Fetch timeline when trip or activeFilter changes
  const prevFilterRef = React.useRef(activeFilter);
  useEffect(() => {
    if (!trip?.id) return;
    // Only clear events when filter changes (not on initial load)
    if (prevFilterRef.current !== activeFilter) {
      setTripEvents([]);
      prevFilterRef.current = activeFilter;
    }
    dispatch(getTimeline({ tripNumber: trip.id.toString(), isActive: activeFilter }));
  }, [trip?.id, activeFilter, dispatch]);

  // Fetch accepted/dismissed stats from the server
  const [acceptedWaitCount, setAcceptedWaitCount] = useState(0);
  const [acceptedWaitMinutes, setAcceptedWaitMinutes] = useState(0);
  const [dismissedWaitCount, setDismissedWaitCount] = useState(0);
  const [dismissedWaitMinutes, setDismissedWaitMinutes] = useState(0);

  useEffect(() => {
    if (!trip?.id) return;
    SuggestedEventsService.getTripStats(trip.id).then((stats) => {
      setAcceptedWaitCount(stats.acceptedWaitCount);
      setAcceptedWaitMinutes(stats.acceptedWaitMinutes);
      setDismissedWaitCount(stats.dismissedWaitCount);
      setDismissedWaitMinutes(stats.dismissedWaitMinutes);
    }).catch((err) => {
      console.error("Failed to load accepted/dismissed wait stats", err);
      // Re-runs on every events change; the fixed toastId keeps a persistent failure to one toast.
      toast.error("Could not load accepted/dismissed wait counts.", { toastId: "closer-trip-stats" });
    });
  }, [trip?.id, tripEvents]);

  // Pre-fetch vehicle trip data for Apply GPS Data (independent of map rendering)
  useEffect(() => {
    if (!trip?.vehicle?.unitNumber || !_tripTimeline?.length) return;

    const startEvent = _tripTimeline.find((t: any) => t.type === "Begin Trip");
    const endEvent = _tripTimeline.find((t: any) => t.type === "End Trip");
    if (!startEvent?.time) return;

    // Pull 12 hours BEFORE Begin Trip so overnight/parked stops that started the
    // night before show up. GeoApi only returns activity whose timestamps fall in
    // the requested window — without the back-pad, an overnight stop's segment is
    // never returned and the client-side overlap filter has nothing to find.
    const start = moment.utc(startEvent.time).subtract(12, "hours");
    const end = endEvent?.time ? moment.utc(endEvent.time) : moment.utc(startEvent.time).add(12, "hours");

    dispatch(
      getVehicleTripData({
        unitNumber: +trip.vehicle.unitNumber,
        startDate: start,
        endDate: end,
        timezone: _timeZone,
        tripNumber: trip.tripNumber,
        clientName: trip.clientName,
      })
    );
  }, [trip?.vehicle?.unitNumber, _tripTimeline, _timeZone, dispatch]);

  // Map data readiness based on pre-fetched Redux data
  useEffect(() => {
    setMapDataReady(!!(_vehicleActivity?.activity?.length || _vehicleActivity?.telemetry?.length));
  }, [_vehicleActivity]);

  // GPS-derived actual move times for Begin Trip and End Trip rows. Computed from the
  // VehicleActivity segments already loaded for the map — no extra API call. Lets
  // dispatchers spot a late button press inline without leaving Flow.
  const [gpsFirstMove, gpsLastMove] = React.useMemo<[string | null, string | null]>(() => {
    const segs = (_vehicleActivity?.activity || []).filter((a: any) => a?.activityState && a.activityState !== "Stopped");
    if (!segs.length) return [null, null];
    const sorted = [...segs].sort(
      (a: any, b: any) => new Date(a.startDateUTC).getTime() - new Date(b.startDateUTC).getTime()
    );
    const first = sorted[0]?.startDateUTC ?? null;
    const last = [...segs].sort(
      (a: any, b: any) => new Date(b.endDateUTC).getTime() - new Date(a.endDateUTC).getTime()
    )[0]?.endDateUTC ?? null;
    return [first, last];
  }, [_vehicleActivity]);

  // Validation function that can be called on demand
  // Susan, 2026-09-25: a trip kicked back by the railroad must not be approvable in Flow while
  // the rejection stands, and the closer must be able to see WHY without opening Dashboard.
  //
  // Injected into validationErrors rather than gated separately: Approve is already disabled on
  // `validationErrors.length > 0`, the panel already lists them, and Override & Approve already
  // appears when there are errors and the user holds AllowOverride. One source of truth for
  // "this trip is not approvable", and someone senior can still push it through.
  //
  // Safe to gate on because the field is self-clearing: sp_BulkApproveTrips and
  // sp_TripApprovalStatusUpdateMultiple both null B2BErrorMessage on approve, so a corrected and
  // re-approved trip cannot be left wedged behind a stale message.
  const [b2bError, setB2bError] = useState<TripB2BError | null>(null);
  useEffect(() => {
    if (!trip?.id) return;
    let cancelled = false;
    tripService
      .getB2BError(trip.id)
      .then((e) => { if (!cancelled) setB2bError(e); })
      .catch(() => { if (!cancelled) setB2bError(null); });
    return () => { cancelled = true; };
  }, [trip?.id, trip?.isApproved]);

  const b2bValidationError = useMemo(() => {
    if (!b2bError?.b2BErrorMessage) return null;
    const expected =
      b2bError.expectedDriveTimeMinutes != null
        ? ` (our records: ${b2bError.fromPoint} to ${b2bError.toPoint} is ${b2bError.expectedDriveTimeMiles} miles and ${b2bError.expectedDriveTimeMinutes} minutes)`
        : "";
    return { message: `Rejected by the railroad: ${b2bError.b2BErrorMessage}${expected}` };
  }, [b2bError]);

  const effectiveValidationErrors = useMemo(
    () => (b2bValidationError ? [b2bValidationError, ...validationErrors] : validationErrors),
    [b2bValidationError, validationErrors],
  );

  const runValidation = useCallback(() => {
    if (!trip?.id) return Promise.resolve();

    setValidationLoaded(false);
    setValidating(true);
    return tripService.validate(trip.id).then((response) => {
      if (response) {
        const allItems = response.errors || [];
        const errors = allItems.filter((e) => e.errorType !== "Warning");
        const warnings = allItems.filter((e) => e.errorType === "Warning");
        setValidationErrors(errors);
        setValidationWarnings(warnings);
        // A railroad rejection has to stay on screen even when OUR rules all pass: driving the
        // panel off response.isValid alone hid it on exactly the trips whose only problem was
        // the B2B error.
        setShowValidationErrors(!response.isValid || warnings.length > 0 || !!b2bValidationError);
        setExpandedValidationErrors(!response.isValid || !!b2bValidationError);

        setCanOverride(response.overrideValidation?.canOverride ?? false);
        setOverrideReason(response.overrideValidation?.reason ?? "");

        setValidationLoaded(true);
      }
    }).finally(() => setValidating(false));
  }, [trip?.id]);

  // Run initial validation when trip loads
  useEffect(() => {
    runValidation();
  }, [runValidation]);

  const saveAndValidateEvents = async (opts?: { keepGridVisible?: boolean }): Promise<boolean> => {
    let eventsToSave = tripEvents;

    // Validate all active events have an event type before saving
    const activeEvents = eventsToSave.filter((e) => e.isActive !== false);
    const eventsWithoutType = activeEvents.filter((e) => !e.typeId);

    if (eventsWithoutType.length > 0) {
      const entryNumbers = eventsWithoutType.map((e) => activeEvents.indexOf(e) + 1).join(", ");
      toast.error(`Cannot save: Event type is required for entry ${entryNumbers}`);
      return false;
    }

    // isLoading swaps the whole grid for a skeleton. That is right for a standalone Save, but
    // when the save is the first half of an approve it blanks the screen -- buttons included --
    // at the exact moment the user is waiting to be told what is happening.
    if (!opts?.keepGridVisible) setIsLoading(true);
    return EventsService.saveAndValidate(trip.id, eventsToSave)
      .then(async (response) => {
        handleValidationResponse(response, "Events saved and validated successfully.", "Events validation");

        // Clear local events to force refresh from backend
        setTripEvents([]);

        // If saving with filter set to inactive, reset to active after save
        // The useEffect will automatically reload the timeline when activeFilter changes
        if (activeFilter === "inactive") {
          setActiveFilter("active");
        } else {
          // Await the reload so Save stays disabled until the timeline is back from the server.
          // Without the await, a rapid second click could re-send stale negative-ID suggestions
          // and produce duplicate Event rows (see trips 6667881 / 6668166 on 2026-05-20).
          await dispatch(getTimeline({ tripNumber: trip.id.toString(), isActive: activeFilter, isReload: true }));
        }

        return true;
      })
      .catch((err) => {
        console.error('[SAVE-ERROR]', err?.response?.status, err?.response?.data, err?.message, err);
        const queuedMessage = conflictMessage(err);
        if (queuedMessage) {
          // Nothing was written and the grid keeps the closer's unsaved edits; the banner explains.
          toast.error(queuedMessage, { autoClose: false });
          refreshTripResetStatus();
        } else {
          toast.error("Events save failed. Please try again.");
        }
        return false;
      })
      .finally(() => {
        if (!opts?.keepGridVisible) setIsLoading(false);
      });
  };

  /**
   * Approve means "approve what I am looking at". The grid can hold GPS suggestions that are not
   * Event rows yet -- Event Reset rebuilds the middle of the trip entirely out of them -- and
   * neither approve button sent them, so the server approved whatever the database happened to
   * hold: on trip 6784225 a rebuilt trip approved as its six original events, suggestions
   * untouched, behind a success toast. Save first, every time, exactly as pressing Save and
   * Validate before Approve would have done. A save that never reached the server stops the
   * approve rather than approving something else.
   */
  const persistBeforeApproval = async (): Promise<boolean> => {
    setApproveLoading(true);
    // One request: the merge saves the events and then runs the rules, so say both. The approve
    // that follows is a second round-trip and gets its own label.
    setApproveStatus("Saving and validating events ...");
    // Header shows a skeleton on the approval fields until the post-approve refetch lands.
    dispatch(setApproving(true));

    const saved = await saveAndValidateEvents({ keepGridVisible: true });
    if (!saved) {
      toast.error("Your events could not be saved, so the trip was not approved.");
      setApproveStatus(null);
      setApproveLoading(false);
      dispatch(setApproving(false));
    }
    return saved;
  };

  // One message for whatever is currently happening to the grid. Ordered by how much the user
  // cares: an approve is what they just asked for, a reset is the slow one, a plain reload is the
  // least interesting. Null means nothing is running and the grid is live.
  const busyMessage = approveStatus
    ? approveStatus.replace(/ \.\.\.$/, "")
    : resetStatus
      ? resetStatus
      : isLoading
        ? "Saving events"
        : timelineLoading
          ? "Loading trip events"
          : validating
            ? "Checking validation rules"
            : null;

  const busyDetail = resetStatus === "Rebuilding events from GPS"
    ? "Reading the vehicle's GPS for the trip window. This can take a few seconds."
    : null;

  const handleValidationResponse = (response, successMessage, errorPrefix) => {
    if (!response) {
      toast.success(successMessage);
      return;
    }
    const allItems = response.errors || [];
    const errors = allItems.filter((e) => e.errorType !== "Warning");
    const warnings = allItems.filter((e) => e.errorType === "Warning");
    setValidationErrors(errors);
    setValidationWarnings(warnings);
    // A railroad rejection has to stay on screen even when OUR rules all pass: driving the
    // panel off response.isValid alone hid it on exactly the trips whose only problem was
    // the B2B error.
    setShowValidationErrors(!response.isValid || warnings.length > 0 || !!b2bValidationError);
    setExpandedValidationErrors(!response.isValid || !!b2bValidationError);
    if (response.isValid && warnings.length === 0) {
      toast.success(successMessage);
    } else if (response.isValid && warnings.length > 0) {
      toast.success(successMessage);
      toast.warning(`${warnings.length} warning${warnings.length === 1 ? "" : "s"} found.`);
    } else {
      toast.warning(
        `${errorPrefix} found ${errors.length} error${errors.length === 1 ? "" : "s"}.`
      );
    }
  };

  const approveTrip = async () => {
    if (!(await persistBeforeApproval())) return;

    setApproveStatus("Approving trip ...");
    tripService
      .finalize(trip.id)
      .then(async (response) => {
        // The finalize response already carries the rule results, so a refused approve needs no
        // second validate -- handleValidationResponse paints them. An approve that went through
        // needs none either: the trip is approved and the grid is read-only from here.
        if (response && response.errors) {
          handleValidationResponse(response, "Trip finalized successfully.", "Trip finalization");
        } else {
          toast.success("Trip approval successfully.");
        }
        await dispatch(getTrip(trip.id));
      })
      .catch((e) => {
        toast.error("Trip approval failed.");
      })
      .finally(() => {
        setApproveLoading(false);
        setApproveStatus(null);
        dispatch(setApproving(false));
      });
  };

  const overrideFinalizeTrip = async () => {
    if (!(await persistBeforeApproval())) return;

    setApproveStatus("Overriding & approving ...");

    tripService
      .overrideFinalize(trip.id, overrideReason)
      .then(async (response) => {
        // Same as approve: the response carries the rule results, and an override that went
        // through has nothing left to check.
        if (response && response.errors) {
          handleValidationResponse(response, "Trip finalized with override successfully.", "Trip override finalization");
        } else {
          toast.success("Trip override approval successfully.");
        }
        await dispatch(getTrip(trip.id));
      })
      .catch((e) => {
        toast.error("Trip override approval failed.");
      })
      .finally(() => {
        setApproveLoading(false);
        setApproveStatus(null);
        dispatch(setApproving(false));
      });
  };

  const reopenTrip = () => {
    setApproveLoading(true);
    setApproveStatus("Re-opening trip ...");
    tripService
      .reopen(trip.id)
      .then(async () => {
        toast.success("Trip re-opened successfully.");
        setApproveStatus("Re-checking validation ...");
        await dispatch(getTrip(trip.id));
        await runValidation();
      })
      .catch((e) => {
        toast.error("Trip re-open failed.");
      })
      .finally(() => {
        setApproveLoading(false);
        setApproveStatus(null);
        dispatch(setApproving(false));
      });
  };

  const deactivateAllEvents = () => {
    const updatedEvents = tripEvents.map(event => ({
      ...event,
      isActive: false
    }));
    setTripEvents(updatedEvents);
    toast.info("All events deactivated");
  };

  // Event Reset. Deletes every event except Begin Trip and End Trip - driver-entered waits
  // included - clears the trip's suggestions and rebuilds the middle from GPS.
  //
  // Gated two ways in the UI: the Admin role, and the trip actually having GPS (no GPS means
  // nothing to rebuild from, so the reset would only destroy). OrderApi enforces both again,
  // plus the closed-trip and Begin/End-anchor requirements, so a stale page cannot strip a
  // trip that cannot be rebuilt.
  // The button only opens the dialog; ConfirmDialog's onAccept runs the reset. Same confirmation
  // component the rest of the app uses (see TripHeaderView's delete-parent), rather than
  // window.confirm — a native modal looks nothing like the product and blocks the whole tab while
  // it is up.
  const handleEventReset = () => {
    if (!trip?.id) return;
    setEventResetConfirmOpen(true);
  };

  const runEventReset = async () => {
    if (!trip?.id) return;

    setEventResetLoading(true);
    setResetStatus("Rebuilding events from GPS");
    try {
      const result = await EventsService.resetTripEvents(trip.id);

      if (!result.wasReset) {
        // Nothing was changed; say why rather than reporting a success that did not happen.
        toast.warning(result.blockedReason || "The trip could not be reset.");
        return;
      }

      // Rebuilding nothing is a real outcome, not a failure - GPS may yield no stops. Report
      // the counts either way so it is clear what actually happened.
      toast.success(
        `Reset complete: removed ${result.deactivatedEventCount} event(s) and ` +
          `${result.removedSuggestionCount} suggestion(s), rebuilt ${result.regeneratedSuggestionCount} from GPS.`
      );
      setResetStatus("Loading rebuilt events");
      await dispatch(getTimeline({ tripNumber: trip.id.toString(), isActive: activeFilter, isReload: true }));

      // A reset replaces the middle of the trip, so every validation answer on screen is about a
      // trip that no longer exists -- and the closer is choosing between Approve and Override
      // Approve on the strength of it. Re-validate like the approve and verify handlers do.
      setResetStatus("Checking validation rules");
      await runValidation();
    } catch (error) {
      console.error("Event Reset failed:", error);
      toast.error("Event Reset failed. The trip was not changed.");
    } finally {
      setEventResetLoading(false);
      setResetStatus(null);
    }
  };

  const verifyTrip = async (verified: boolean) => {
    if (!trip?.id) return;
    setVerifyLoading(true);
    try {
      await tripService.verify(trip.id, verified);
      toast.success(verified ? "Trip verified." : "Trip unverified.");
      await dispatch(getTrip(trip.id));
    } catch (e) {
      toast.error("Verify trip failed.");
    } finally {
      setVerifyLoading(false);
    }
  };

  const setManagerReview = async (mapped: boolean) => {
    if (!trip?.id) return;
    setManagerReviewLoading(true);
    try {
      await tripService.setManagerReview(trip.id, mapped);
      toast.success(mapped ? "Manager review set." : "Manager review cleared.");
      await dispatch(getTrip(trip.id));
    } catch (e) {
      toast.error("Manager review update failed.");
    } finally {
      setManagerReviewLoading(false);
    }
  };

  // Export events to Excel
  const handleExport = () => {
    if (!trip?.id) return;
    setExportLoading(true);
    EventsService.getTripEventsExport(trip.id, _timeZone)
      .then((data: any) => {
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Trip_${trip.id}_Events.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(() => {
        toast.error("Failed to export events");
      })
      .finally(() => setExportLoading(false));
  };

  // Handle toggling Active checkbox — persist immediately and reload grid
  const handleToggleEventActive = async (event: any, isActive: boolean) => {
    // Remove from list immediately — no refresh to avoid interrupting the user
    setTripEvents((prev) => prev.filter((e: any) => e.id !== event.id));

    try {
      if (event.isSuggested) {
        await SuggestedEventsService.setSuggestedEventActive(event.suggestedEventId, isActive);
      } else if (event.eventId > 0) {
        await EventsService.setEventActive(event.eventId, isActive);
      }
    } catch (error) {
      console.error("Failed to toggle event active:", error);
      const queuedMessage = conflictMessage(error);
      if (queuedMessage) {
        toast.error(queuedMessage, { autoClose: false });
        refreshTripResetStatus();
      } else {
        toast.error("Failed to update event");
      }
      // On failure, reload to restore the event
      dispatch(getTimeline({ tripNumber: trip.id.toString(), isActive: activeFilter, isReload: true }));
    }
  };

  const handleGoToMap = (eventId: number) => {
    const url = `/maps/popup?tripNumber=${trip?.id}&eventId=${eventId}`;
    window.open(url, `map-${trip?.id}`, "width=1200,height=800,resizable=yes,scrollbars=yes");
  };

  return (
    <Box sx={{ mt: 2, overflow: "hidden", height: "100%" }}>
      <TripResetQueuedBanner status={tripResetStatus} />
      {!queuedForReset && <TripResetChip status={tripResetStatus} canOpenHistory={isAdmin} size="small" sx={{ mb: 1 }} />}
      <CloserEventsGrid
        tripId={trip?.id}
        isClosed={trip?.isClosed}
        readOnly={(!trip?.isClosed && !hasAllowApprove) || !!trip?.isApproved || queuedForReset}
        gpsFirstMove={gpsFirstMove}
        gpsLastMove={gpsLastMove}
        tripEvents={tripEvents}
        setTripEvents={setTripEvents}
        eventTypes={eventTypes}
        waitReasons={waitReasons}
        pointsOfService={_pointsOfService}
        timeZone={_timeZone}
        b2bError={b2bError}
        validationErrors={effectiveValidationErrors}
        validationWarnings={validationWarnings}
        showValidationErrors={showValidationErrors}
        setShowValidationErrors={setShowValidationErrors}
        expandedValidationErrors={expandedValidationErrors}
        setExpandedValidationErrors={setExpandedValidationErrors}
        isLoading={isLoading || timelineLoading}
        approveLoading={approveLoading}
        approveStatus={approveStatus}
        busyMessage={busyMessage}
        busyDetail={busyDetail}
        onApprove={approveTrip}
        onSaveAndValidate={saveAndValidateEvents}
        gridCollapsed={gridCollapsed}
        setGridCollapsed={setGridCollapsed}
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        canOverride={canOverride}
        overrideReason={overrideReason}
        onOverrideFinalize={overrideFinalizeTrip}
        onDeactivateAll={deactivateAllEvents}
        validationLoaded={validationLoaded}
        hasAllowApprove={hasAllowApprove}
        hasAllowOverride={hasAllowOverride}
        hasAllowClearAllEvents={hasAllowClearAllEvents}
        isApproved={trip?.isApproved}
        onReopen={reopenTrip}
        onExport={handleExport}
        exportLoading={exportLoading}
        allowApplyGpsOdometers
        allowEventReset={canResetEvents}
        onEventReset={handleEventReset}
        eventResetLoading={eventResetLoading}
        onGoToMap={handleGoToMap}
        mapDataReady={mapDataReady}
        isVerified={trip?.tripReview?.isVerified}
        onVerify={verifyTrip}
        verifyLoading={verifyLoading}
        isMapped={trip?.tripReview?.isMapped}
        onSetManagerReview={setManagerReview}
        managerReviewLoading={managerReviewLoading}
        onToggleEventActive={handleToggleEventActive}
        acceptedWaitCount={acceptedWaitCount}
        acceptedWaitMinutes={acceptedWaitMinutes}
        dismissedWaitCount={dismissedWaitCount}
        dismissedWaitMinutes={dismissedWaitMinutes}
      />

      <ConfirmDialog
        open={eventResetConfirmOpen}
        setOpen={setEventResetConfirmOpen}
        title="Event Reset"
        subTitle={
          "This deletes every event except Begin Trip and End Trip — including waits entered by " +
          "the driver — and rebuilds the trip from GPS. This cannot be undone."
        }
        onAccept={runEventReset}
      />

    </Box>
  );
};
export default CloserLayout;